import React, { useState } from "react";
import axios from "axios";
import { Plane, RefreshCw, Trash2, Bell, BellOff, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../../contexts/AuthContext";
import { FlightStatusBadge } from "./FlightStatusBadge";
import { BoardingCountdown } from "./BoardingCountdown";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA  = { fontFamily: "'Lora', Georgia, serif" };

function formatTime(isoStr) {
  if (!isoStr) return "";
  // AeroDataBox returns "2026-05-31 10:20+02:00" (space) or ISO "T" format
  const normalized = isoStr.replace("T", " ");
  const timePart = normalized.split(" ")[1] || "";
  return timePart.slice(0, 5);
}

export function FlightCard({ flight, tripId, onRefreshed, onDeleted }) {
  const [refreshing, setRefreshing] = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(flight.notifications_enabled);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/trips/${tripId}/flights/${flight.id}/refresh`);
      onRefreshed?.(data);
      toast.success("Vol actualisé");
    } catch {
      toast.error("Actualisation impossible");
    } finally { setRefreshing(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer le vol ${flight.flight_number} du tracking ?`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/trips/${tripId}/flights/${flight.id}`);
      onDeleted?.(flight.id);
      toast.success("Vol supprimé");
    } catch {
      toast.error("Impossible de supprimer");
    } finally { setDeleting(false); }
  };

  const handleToggleNotif = async () => {
    const newVal = !notifEnabled;
    if (newVal && "Notification" in window) {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications refusées par le navigateur");
        return;
      }
    }
    try {
      await axios.patch(`${API}/trips/${tripId}/flights/${flight.id}/notifications`, { enabled: newVal });
      setNotifEnabled(newVal);
      toast.success(newVal ? "Alertes activées pour ce vol" : "Alertes désactivées");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const depTime     = formatTime(flight.dep_revised || flight.dep_scheduled);
  const depSched    = formatTime(flight.dep_scheduled);
  const depRevised  = flight.dep_revised ? formatTime(flight.dep_revised) : null;
  const isDelayed   = depRevised && depRevised !== depSched;

  const cacheLabel = flight.from_cache
    ? `Hors ligne · Données du ${flight.last_fetched_at ? new Date(flight.last_fetched_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "–"}`
    : flight.last_fetched_at
      ? `Mis à jour il y a ${Math.round((Date.now() - new Date(flight.last_fetched_at).getTime()) / 60000)} min`
      : "";

  return (
    <div
      className="border border-[#DCD4C6] rounded-sm overflow-hidden"
      data-testid={`flight-card-${flight.flight_number}`}
    >
      {/* Offline banner */}
      {flight.stale && (
        <div className="bg-amber-50 border-b border-amber-100 px-4 py-1.5 flex items-center gap-2">
          <span className="text-xs text-amber-700" style={LORA}>Hors ligne · Dernière mise à jour : {cacheLabel}</span>
        </div>
      )}

      {/* Header */}
      <div className="px-4 pt-4 pb-3 bg-[#F9F6F0]">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <p className="text-xs text-[#8B5E34] uppercase tracking-widest mb-0.5" style={LORA}>
              {flight.segment_label}
            </p>
            <h3 className="text-2xl leading-none text-[#2B2623]" style={{ ...SERIF, fontWeight: 700 }}>
              {flight.flight_number}
            </h3>
            {flight.airline_name && (
              <p className="text-xs text-[#9A8F86] mt-0.5" style={LORA}>{flight.airline_name}</p>
            )}
          </div>
          <FlightStatusBadge status={flight.status} />
        </div>

        {/* Route */}
        <div className="flex items-center gap-2 mt-3">
          <div className="text-center">
            <p className="text-lg font-bold text-[#2B2623]" style={SERIF}>{flight.departure_airport_code || "—"}</p>
            {flight.departure_airport_name && (
              <p className="text-xs text-[#9A8F86] max-w-[90px] leading-tight text-center" style={LORA}>{flight.departure_airport_name}</p>
            )}
          </div>
          <ArrowRight size={14} strokeWidth={1.5} className="text-[#C17F59] flex-shrink-0" />
          <div className="text-center">
            <p className="text-lg font-bold text-[#2B2623]" style={SERIF}>{flight.arrival_airport_code || "—"}</p>
            {flight.arrival_airport_name && (
              <p className="text-xs text-[#9A8F86] max-w-[90px] leading-tight text-center" style={LORA}>{flight.arrival_airport_name}</p>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-2 bg-white/50 border-t border-[#DCD4C6]">
        {/* Times */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-[#8B5E34] uppercase tracking-widest mb-0.5" style={LORA}>Départ</p>
            <div className="flex items-baseline gap-1.5">
              <p className="text-base font-semibold text-[#2B2623]" style={SERIF}>{depSched || "—"}</p>
              {isDelayed && (
                <p className="text-sm text-amber-600 font-semibold" style={SERIF}>{depRevised}</p>
              )}
            </div>
          </div>
          <div>
            <p className="text-xs text-[#8B5E34] uppercase tracking-widest mb-0.5" style={LORA}>Arrivée</p>
            <p className="text-base font-semibold text-[#2B2623]" style={SERIF}>
              {formatTime(flight.arr_revised || flight.arr_scheduled) || "—"}
            </p>
          </div>
        </div>

        {/* Gate / Terminal */}
        {(flight.dep_gate || flight.dep_terminal) && (
          <div className="flex items-center gap-4">
            {flight.dep_terminal && (
              <div>
                <p className="text-xs text-[#8B5E34] uppercase tracking-widest" style={LORA}>Terminal</p>
                <p className="text-sm font-semibold text-[#2B2623]" style={SERIF}>{flight.dep_terminal}</p>
              </div>
            )}
            {flight.dep_gate && (
              <div>
                <p className="text-xs text-[#8B5E34] uppercase tracking-widest" style={LORA}>Porte</p>
                <p className="text-2xl font-bold text-[#C17F59]" style={SERIF}>{flight.dep_gate}</p>
              </div>
            )}
          </div>
        )}

        {/* Boarding countdown */}
        {flight.boarding_countdown && <BoardingCountdown countdown={flight.boarding_countdown} />}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-[#DCD4C6] bg-[#F9F6F0] flex items-center justify-between gap-2">
        <p className="text-xs text-[#9A8F86] italic truncate" style={LORA}>{cacheLabel}</p>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Notifications toggle */}
          <button
            onClick={handleToggleNotif}
            className={`flex items-center gap-1 text-xs px-2 py-1.5 border rounded-sm transition-colors ${
              notifEnabled
                ? "bg-[#2B2623] text-[#F9F6F0] border-[#2B2623]"
                : "text-[#5C554E] border-[#DCD4C6] hover:border-[#C17F59]"
            }`}
            style={LORA}
            title={notifEnabled ? "Désactiver les alertes" : "Activer les alertes"}
            data-testid={`notif-toggle-${flight.id}`}
          >
            {notifEnabled ? <Bell size={11} strokeWidth={1.5} /> : <BellOff size={11} strokeWidth={1.5} />}
            Alertes
          </button>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 text-xs px-2 py-1.5 border border-[#DCD4C6] text-[#5C554E] hover:border-[#C17F59] hover:text-[#C17F59] disabled:opacity-50 rounded-sm transition-colors"
            style={LORA}
            data-testid={`flight-refresh-${flight.id}`}
          >
            <RefreshCw size={11} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} />
            Actualiser
          </button>

          {/* Delete */}
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-1.5 border border-[#DCD4C6] text-[#9A8F86] hover:border-red-300 hover:text-red-500 disabled:opacity-50 rounded-sm transition-colors"
            data-testid={`flight-delete-${flight.id}`}
            title="Supprimer ce vol"
          >
            <Trash2 size={11} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
